import { STORY_SOURCE, story } from '@/content/storyBuilder'

// The second written page, and the first with photography. Everything below
// the header is transcribed from the captures of digitopia.co/customers/arzum/
// sent on 2026-08-27 and **re-read against a four-part, full-width capture of
// the same page on 2026-08-28** ("tastamam şekilde, aynısını, tıpkısını
// tasarla"). The first captures were 373px wide — body type about four pixels
// tall — and that second reading corrected six things the pixels had got
// wrong: the mobile application is *Arzum Seninle* and not "Arzum Yanımda",
// the company operates in 43 countries and not 51, 25% of its export volume
// goes to the EU and not 20%, the Digital Eyes movement is about 3 years old
// and not 4, the Project Management Office *predetermines* the trends (the
// word the old check guessed at as "ponderizes"), and Arzum *contracted*
// Digitopia rather than contacting them.
//
// Its shape is now the reference's rather than the template's usual, which is
// the other half of that request. "Who is Arzum?" is a centred section
// carrying the company paragraph and the photograph, not an `intro` grey band
// above them — the reference runs that opening on the white page under its own
// heading — and the three paragraphs after the picture are a headless section
// continuing it. "The Outcome" lost its `band` for the same reason: the
// reference sets every section on the page and keeps the one grey panel for
// the quote at the foot. That is LC Waikiki's shape, arrived at from the same
// kind of capture the day before.
//
// The closing quote is not repeated as a `section` — `content/testimonials.ts`
// already holds Arif Emre Unal's words keyed to this slug, with Arzum's mark
// and his portrait, and `StoryBody` draws that card under the story. That is
// the testimonial template the request asked for, and it needed no new media:
// the two files sent with it are both smaller than what this repository
// already holds (`portrait-arif-emre-unal.png` is 900×900 against 600×600,
// and in the monochrome the whole portrait set is cut in;
// `arzum-kitchen-appliances.webp` is 1320×500 against 1200×455).
export const added = story('arzum', 'Arzum', {
  doc: 'a2c8b350-f50f-4691-992a-625e0eb4df47-Arzum and Digitopia.pdf',
  video: 'AYk_IEgLDL8',
  lede: ['Digital Eyes on the Digital Prize.'],
  sections: [
    {
      heading: 'Who is Arzum?',
      centred: true,
      body: [
        'Arzum has been in the small household appliance business for 55 years, since 1966. '
        + 'Arzum ranks first in the market according to the number of units sold. In 2020 Arzum '
        + 'is the market leader in food preparation, cooking, frying, Turkish coffee making, '
        + 'and hair care categories. Overall, Arzum provides 650 products in 6 different '
        + 'categories, including cleaning, personal care and ironing to millions of consumers '
        + 'in Turkey and around the world.',
      ],
      photo: {
        src: '/photo/arzum-kitchen-appliances.webp',
        alt: 'A row of Arzum small household appliances on a kitchen counter: blender, '
          + 'microwave, coffee grinder, waffle maker, toaster and kettle.',
      },
    },
    {
      body: [
        'It is dedicated to spreading their native Turkish coffee culture to countries all over '
        + 'the world on the wings of the motto “The Brand that Makes the World Love Turkish '
        + 'Coffee”. Arzum carries out its business operations over five main sales channels: '
        + 'technology, supermarket/hypermarket and discount chain stores; e-commerce and '
        + 'retailer/wholesale traditional channels; all contributing to their global sales. '
        + 'Arzum provides aftersales services in an all-in-one approach through its Customer '
        + 'Care Portal.',
        'Arzum offers a 4-year warranty for registered products. The company’s mobile '
        + 'application, Arzum Seninle, is a tool designed to make product registration easier '
        + 'and more convenient for consumers. Arzum exports its products under its own brand.',
        'Arzum operates in 43 countries across the Middle East, North Africa and the USA '
        + 'regions. The company has a particular focus on Egypt, Saudi Arabia, Kuwait and '
        + 'Germany with 25% of its export volume sent to the European Union.',
      ],
    },
    {
      heading: 'Project Background',
      body: [
        'In Arzum’s own words…',
        'At Arzum, we have been making investments in digital transformation via internal '
        + 'resources for about 3 years while cultivating our “Digital Eyes” movement. Digital '
        + 'Eyes consists of a team of 10 to 15 people, working directly within the company’s '
        + 'internal Digital Transformation and Project Management Office. The Digital '
        + 'Transformation and Project Management Office predetermines the trends Arzum will '
        + 'focus on each year with the involvement of our participating teammates. Throughout '
        + 'this work, Arzum had increased technological literacy within the company and '
        + 'contributed to the digital transformation process through our internal resources.',
      ],
    },
    {
      heading: 'So, What was Missing?',
      body: [
        'We had been receiving a positive return from our work with Digital Eyes. However, '
        + 'there was a benchmark missing. We did not have any knowledge about where we were in '
        + 'the market or whether our competitors were focusing on similar areas. We did not '
        + 'know if where we stood in the market average compared to our competitors. It was at '
        + 'that point when a digital maturity study became a necessity. Through measuring our '
        + 'digital maturity, we would be able to explore the possibility of even better digital '
        + 'outcomes for our company’s future digital transformation success.',
        'We had heard about the Digitopia founders and the competencies of their delivery team '
        + 'from other the companies in the market. Such positive references led us to contract '
        + 'Digitopia and utilise their Digital Maturity Index.',
        'The digital maturity measurement process was a smooth experience for us. The Digitopia '
        + 'delivery team took their time to understand Arzum’s inner dynamics before starting '
        + 'the first initiative. They internalized our company’s distinctive business model and '
        + 'managed the process accordingly and we felt this difference from the beginning of '
        + 'the first session to the very last one. Applying a coach-like style, Digitopia '
        + 'empowered us to evaluate ourselves in a very short time by asking the right '
        + 'questions to the right people. Focusing on six different dimensions, they conducted '
        + 'separate studies for each vertical. In this way, we could evaluate our digital '
        + 'transformation efforts in terms of where we were in the market, and what point we '
        + 'aim to reach. They brought these six separately held sessions together and generated '
        + 'a roadmap, where our priorities were indicated. At Arzum, we believe we have '
        + 'increased the efficiency of the process by following Digitopia’s suggested roadmap '
        + 'starting at the executive board level.',
      ],
    },
    {
      heading: 'The Outcome',
      body: [
        'Digitopia’s work with Arzum has created both a top-down and a bottom-up approach, '
        + 'enabling the board and the team to achieve efficient common ground for mutual '
        + 'success.',
        'From the moment we completed the Digital Maturity Index, we were able to comprehend '
        + 'clearly how we should act on our digital transformation strategy moving forward. '
        + 'This recognition provided us great convenience while preparing our budget and '
        + 'company strategy. The Digital Eyes initiative continues, and is now pursuing their '
        + 'work in a more enriched and focused environment.',
        'Digital trends are changing quickly, and we see that Digitopia has provided us with a '
        + 'detailed, customer-tailored roadmap for the next 3 years. According to the current '
        + 'results, we have exhibited a balanced distribution across 6 dimensions, and this '
        + 'makes us very happy and hopeful for our digital future at Arzum. We aim to update '
        + 'our work in this field every two years to maintain this balance, we have planned '
        + 'across DMI’s six dimensions.',
        'Thank you to Digitopia for a great and reassuring Digital Maturity Index service '
        + 'experience.',
      ],
    },
  ],
  check:
    'This page’s words were read off screenshots rather than copied out of a file we hold, so '
    + 'they still want a proofread against '
    + `${STORY_SOURCE}/arzum/ before production — but the reading is a much better one than it `
    + 'was. The first captures (2026-08-27) were 373px wide, with body type about four pixels '
    + 'tall; a four-part full-width capture on 2026-08-28 replaced the guesses with the page: '
    + 'the app is “Arzum Seninle” (not “Arzum Yanımda”), it is 43 countries (not 51), 25% of '
    + 'export volume to the EU (not 20%), “about 3 years” of Digital Eyes (not 4), the Project '
    + 'Management Office “predetermines” the trends (the old note flagged this as not a word), '
    + 'and Arzum “contracted” Digitopia rather than contacting them. What is left to confirm is '
    + 'the arithmetic in the opening paragraph — “55 years”, “since 1966”, “In 2020”, “650 '
    + 'products”, “6 different categories” — which the reference itself does not reconcile: 55 '
    + 'years after 1966 is 2021, so one of those three numbers has aged rather than being '
    + 'mistyped, and it is somebody’s call which to update. Three things in the source were '
    + 'left as they are rather than silently edited in a customer’s quoted words: “did not know '
    + 'if where we stood”, “from other the companies”, and “In 2020 Arzum is”. Three the other '
    + 'way, all of them plain typographic slips on the reference and none of them a claim: it '
    + 'sets “provied us great convenience” and “custome-tailored roadmap”, which are “provided” '
    + 'and “customer-tailored” here, and it writes “a 4 year warranty” without the hyphen. '
    + 'Two deliberate differences of shape from the reference, each of them a template-wide '
    + 'decision rather than this page’s: the header eyebrow reads “Digitopia / Customer Success '
    + 'Story” where digitopia.co reads “SUCCESS STORIES”, and the reference’s centred '
    + '“Download” button below “The Outcome” is here the one “Read the full story (PDF)” button '
    + 'in the header, pointing at the same document. One thing to decide rather than check: the '
    + 'quote card at the foot is the Hall of Fame template as asked for, and it takes its '
    + 'attribution from digitopia.co’s own Testimonials page — “Executive Board Member, CIO & '
    + 'COO” — where this story page reads “COO & Executive Committee Member”. Two of their '
    + 'pages disagree about Mr Unal’s title; the imported one won because it is the version we '
    + 'hold as a file, and it is shared with the Hall of Fame, so changing it changes both. '
    + 'Last, the opening sentences of the third paragraph under “So, What was Missing?” are the '
    + 'same words as his quote in that card — the reference page repeats them too, but if that '
    + 'reads as a duplication here, the fix is to trim the paragraph, not to drop the card.',
})

/** Its place in the hand-placed order the imported Customers page set. */
export const order = 14
